/**
 * dashboardSummary.ts — patient dashboard "what happens next" presentation.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: patient dashboard experience.
 *
 * WHY: after status-copy normalisation, the dashboard still listed three equal
 * cards with no hierarchy. This helper derives a patient-safe summary from the
 * live `DashboardData` wire shape only — no invented workflows, no enum changes.
 * Payment wording stays aligned with checkout `paymentCopy.ts` (held ≠ paid).
 */
import type { BillSummary, DashboardData, OrderSummary, SubscriptionState } from '../api/types';
import type { BadgeTone } from '../ui/kit';
import { formatUtcDate } from './format';

/** Presentation kinds the dashboard summary knows how to render. */
export type DashboardSummaryKind =
  | 'empty'
  | 'awaiting_review'
  | 'active_confirmed'
  | 'rejected'
  | 'cancelled'
  | 'payment_attention'
  | 'payment_incomplete'
  | 'preauth_expired'
  | 'generic';

/**
 * WHAT WE KNOW ABOUT CLINICAL EVIDENCE THE PATIENT STILL OWES — AN INPUT TO THE
 * SUMMARY, NOT A SECOND OPINION BESIDE IT (2026-09-05, live defect).
 *
 * THE BUG THIS TYPE EXISTS TO KILL: we emailed patients asking for a weight
 * photo, they signed in, and the hero said "No action is needed right now."
 * while the upload card sat directly above it asking for the photo. Two
 * sources — `dashboardSummary(data)` reading plan/order/bill state, and a
 * separate `getWeightPhotoStatus()` read feeding a `photoPending` boolean that
 * gated the card — neither of which could see the other. Whenever evidence was
 * owed the two contradicted each other, and the reassuring one was on top.
 *
 * Passing the evidence state IN is the whole fix: one function, one answer,
 * and the caller gates the card on the SAME answer (`showEvidencePrompt`), so
 * a disagreement is no longer expressible.
 *
 * FOUR STATES, BECAUSE "WE DO NOT KNOW" IS NOT "NOTHING IS OWED". The status
 * read can fail (500, timeout, an auth blip), and a failed read used to render
 * byte-identically to a patient who owes nothing — a fully reassuring clinical
 * dashboard with no trace that anything had been asked for. `'unknown'` says so
 * out loud; it must never invent an obligation either, because we genuinely do
 * not know whether one exists.
 *
 * Today the only evidence source is the deferred weight photo
 * (`GET /v1/clinic/assessments/mine/weight-photo` → `{ photoPending }`). There
 * is no patient-facing read for "a clinician has asked you for more evidence"
 * — see the note on `showEvidencePrompt` for what the server would have to add.
 */
export type EvidenceState =
  /** The status read is still in flight. Say nothing either way — yet. */
  | 'checking'
  /** The read succeeded and the patient owes evidence. */
  | 'owed'
  /** The read succeeded and nothing is owed. Copy is IDENTICAL to pre-2026-09-05. */
  | 'none'
  /** The read FAILED. We do not know. Never "all clear", never an invented ask. */
  | 'unknown';

export interface DashboardSummary {
  kind: DashboardSummaryKind;
  /** Primary h2 under "Your dashboard". */
  headline: string;
  /** One short supporting paragraph. */
  body: string;
  /** Optional payment reassurance / warning line. */
  paymentNote?: string;
  /** What the patient should do (or not do) next. */
  nextStep: string;
  tone: BadgeTone;
  /** Short badge label shown in the summary card. */
  badgeLabel: string;
  /**
   * The evidence line for the hero — the ask itself, or the honest "we could
   * not check". Absent for `'none'` and `'checking'`: there is nothing to say.
   */
  evidenceNote?: string;
  /**
   * THE EVIDENCE'S OWN ACTION LINE — SET WHENEVER `evidenceNote` IS, AND
   * INDEPENDENTLY OF WHETHER IT WON THE `nextStep` SLOT (2026-09-05 review).
   *
   * `nextStep` is a CONTESTED slot: the evidence step only takes it when the
   * base step was passive (see PASSIVE_NEXT_STEPS), because "Return to
   * checkout" outranks a photo. That contest is right for the hero, which has
   * exactly one next-step callout — but it made the evidence action
   * unreachable for any surface that does not render the hero at all.
   *
   * The empty account is exactly that surface, and it is where the omission
   * bit: `'empty'`'s base step ("Start your 2-minute eligibility check…") is
   * NOT passive, so on a failed status read the admission's retry-and-contact
   * route lost the contest and then had no second way out. The dashboard's
   * empty arm renders no hero, so a patient with a submitted assessment, no
   * order yet and a 500 on the status read was told nothing whatsoever.
   *
   * So the two questions are separated: "what is the evidence asking of the
   * patient" (this field, always) and "does it also own the hero's one callout"
   * (`nextStep`, passive-gated, unchanged). A surface picks the one it can
   * render. Absent for `'none'` and `'checking'`, in step with `evidenceNote`.
   */
  evidenceStep?: string;
  /**
   * MUST be the only gate on the evidence upload card. The dashboard used to
   * mount it straight off the raw `photoPending` boolean, which is exactly how
   * the card and the hero came to disagree.
   *
   * True ONLY for `'owed'`, and only on a summary kind where an ask is
   * coherent (see EVIDENCE_ASK_KINDS). `'unknown'` deliberately does NOT mount
   * it: the card's own heading is "Add your weight photo", which asserts an
   * obligation we cannot stand behind.
   *
   * [SERVER GAP] This is one boolean about one deferred photo. A clinician who
   * uses the staff `POST .../evidence-request` lane produces nothing a patient
   * can read, so the dashboard cannot name WHAT was asked for or WHEN. When
   * clinic-service exposes a patient read for outstanding evidence requests,
   * widen `EvidenceState` to carry the ask rather than growing a second
   * boolean beside this one.
   */
  showEvidencePrompt: boolean;
  /**
   * THE OPTIONAL, PATIENT-INITIATED ROUTE — "I was asked for a photo, here is
   * where I send it" (2026-09-06). NEVER true at the same time as
   * `showEvidencePrompt`: one surface, one ask, and two upload cards on one
   * page is the same contradiction this module was written to make
   * inexpressible.
   *
   * It asserts NOTHING about what is owed, which is the whole reason it can be
   * offered without a server read behind it. The card it mounts says "if we
   * have asked you for a photo", not "your clinician needs a photo".
   *
   * ── [SERVER CONTRACT] WHAT WOULD MAKE THIS AN ASK ─────────────────────────
   * clinic-service must widen the patient read, because nothing the portal can
   * see today distinguishes "a clinician asked for evidence" from "nothing is
   * owed". Precisely:
   *
   *   GET /v1/clinic/assessments/mine/weight-photo
   *     { photoPending: boolean,            // unchanged, the legacy q14 marker
   *       evidenceRequest: null | {
   *         requestedAt: string,            // ISO UTC — "asked on the 3rd"
   *         notifiedAt: string | null } }   // when the patient was actually
   *                                         // TOLD, or null if they never were
   *
   * ⚠️ BOTH FIELDS ALREADY EXIST ON THE MODEL — this widening needs no schema
   * change. An earlier draft of this block asked for a `note` carrying "the
   * clinician's own words to the patient". THERE IS NO SUCH COLUMN:
   * `WeightEvidenceRequest` is `{ requestedByRole, requestedByUserId,
   * requestedAt, satisfiedAt, notifiedAt }` and nothing else. Specifying it
   * here would have been a contract against a field that does not exist —
   * the exact class of false claim this estate has been bitten by. Adding a
   * note is a separate change with its own decisions (who may write it, is it
   * sanitised, does it also ride the email), not a rider on this read.
   *
   * ⚠️ SHOW `requestedAt` ONLY WHEN `notifiedAt` IS SET. A row with a null
   * `notifiedAt` records an internal decision the patient may never have heard
   * about — the schema says so at the column. Telling someone "we asked you on
   * the 3rd" for a request that was never delivered accuses them of ignoring an
   * email that never arrived. Undelivered requests fall back to the generic
   * door below, which asserts nothing.
   *
   * `evidenceRequest` is the OUTSTANDING one (`satisfiedAt: null`) on the
   * caller's own latest submitted assessment — the same row
   * `hasOutstandingEvidenceRequest` already reads inside every photo lane, so
   * this is a read the service can answer from an index it already maintains,
   * self-scoped by the verified principal exactly as `weightPhotoStatusMine` is
   * (R41: the assessment id is never an input).
   *
   * A SEPARATE OBJECT AND NOT A SECOND BOOLEAN, deliberately: the two are
   * different facts with different lifetimes (a deferral is answered by any
   * photo; a request is answered by `satisfiedAt`), and the whole point of the
   * widening is to be able to say WHAT was asked and WHEN. When it lands, widen
   * `EvidenceState` from four words to carry the ask itself, fold it into
   * `withEvidence` beside `photoPending`, and this field goes back to being a
   * convenience rather than the only route.
   */
  showEvidenceSelfServe: boolean;
}

function latestOrder(orders: OrderSummary[]): OrderSummary | null {
  return orders[0] ?? null;
}

function hasPhase(bills: BillSummary[], phase: string): boolean {
  return bills.some((b) => b.phase === phase);
}

/**
 * "Is a payment on this plan failing?" — THE HERO AND THE PLAN CARD ASK THIS
 * QUESTION ONCE (2026-09-05 review, defect 4).
 *
 * The hero's `payment_attention` branch and the stop-months term sentence must
 * agree, because they sit six inches apart on the same screen; they cannot
 * agree if one of them keys on `plan.state === 'past_due'` and the other on
 * `plan.state === 'past_due' || hasPhase(bills, 'failed')`. The term sentence
 * keyed on the narrower half, so a `failed` bill on an `active` plan put "the 2
 * months still to run are charged as they fall due, and the treatment for each
 * of them is still sent to you" directly under "We could not take a payment on
 * your plan".
 *
 * ⚠️ THE OLD DEFENCE — "orders never writes `phase: 'failed'`, so that limb
 * never fires" — IS A CLAIM ABOUT ANOTHER SERVICE, AND THIS REPO CANNOT ENFORCE
 * IT. `BillPhase` (api/types.ts) admits `'failed'`, `patientStatusCopy` renders
 * it ("Payment failed", danger tone) and `BillsCard` will happily list it, so
 * the portal is already built to display a row it argues cannot exist. A shared
 * predicate costs nothing and removes the argument.
 */
function paymentIsFailing(plan: { state: SubscriptionState } | null | undefined, bills: BillSummary[]): boolean {
  // A cancelled plan is not "failing" — it is over, and the hero says so in its
  // own branch. Mirrors the hero's `plan?.state !== 'cancelled'` conjunct.
  if (plan?.state === 'cancelled') return false;
  return plan?.state === 'past_due' || hasPhase(bills, 'failed');
}

/** Clinician-reject payment line — the hold is released; they were not charged. */
function notChargedNote(bills: BillSummary[]): string {
  if (hasPhase(bills, 'released')) return 'Any payment hold has been released — you have not been charged.';
  if (hasPhase(bills, 'held')) return 'If a hold was placed on your card, it will be released and you will not be charged.';
  return 'You have not been charged for this treatment.';
}

/**
 * Cancelled-plan payment line. Never claim "not charged" after a capture —
 * billing history is the source of truth. A refund, if present, is named.
 */
function cancelledPaymentNote(bills: BillSummary[]): string | undefined {
  if (hasPhase(bills, 'refunded')) return 'Any payment for this treatment has been refunded.';
  if (hasPhase(bills, 'released')) return 'Any payment hold has been released — you have not been charged.';
  if (hasPhase(bills, 'held')) return 'If a hold was placed on your card, it will be released and you will not be charged.';
  return undefined;
}

/**
 * THE THREE PASSIVE NEXT STEPS, BY IDENTITY — the lines that tell a patient to
 * sit still. These are the ONLY ones an evidence ask is allowed to overwrite,
 * and matching them by constant (not by kind, and certainly not by regex) is
 * what keeps that list honest: add a fourth passive branch and you have to add
 * it here on purpose. `'No action is needed right now.'` is the exact sentence
 * a patient we had emailed for a photo was reading.
 */
const NO_ACTION_NEEDED = 'No action is needed right now.';
const TRACK_BELOW = 'Track your plan, orders and billing below.';
const REVIEW_DETAILS = 'Review the details below.';
const PASSIVE_NEXT_STEPS: ReadonlySet<string> = new Set([NO_ACTION_NEEDED, TRACK_BELOW, REVIEW_DETAILS]);

/**
 * The summary kinds on which surfacing an evidence ask is COHERENT.
 *
 * Excluded on purpose: `rejected`, `cancelled`, `payment_attention` and
 * `preauth_expired`. Each of those has already told the patient something
 * larger — their treatment was refused, their plan is over, their payment
 * failed, their authorisation lapsed — and every one of them ends "contact
 * us". Asking the same patient, in the same breath, to upload a weight photo
 * for a course of treatment that is not going ahead is a worse message than
 * the bug being fixed here. It is also unanswerable from this repo whether the
 * server clears the deferral marker when an order is rejected, so the pending
 * flag on a dead case may simply be stale.
 *
 * Note this cuts BOTH the hero line and the upload card, together — which is
 * the point: they move as one.
 */
const EVIDENCE_ASK_KINDS: ReadonlySet<DashboardSummaryKind> = new Set<DashboardSummaryKind>([
  'empty',
  'awaiting_review',
  'active_confirmed',
  'payment_incomplete',
  'generic',
]);

/**
 * WHERE A PATIENT MAY OFFER A PHOTO WE NEVER ASKED FOR (2026-09-06).
 *
 * ══════════════════════════════════════════════════════════════════════════
 * ⚠️ [SERVER GAP] THE SELF-CERTIFIED PATIENT COULD NOT REACH THE UPLOAD.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * clinic-service ACCEPTS a patient upload while an evidence request is
 * outstanding — `assertPhotoLaneOpen` was deliberately relaxed on 2026-09-04 so
 * that a clinician asking for evidence mid-treatment does not 409 every lane
 * the patient could answer on. But the only patient-facing READ,
 * `GET /v1/clinic/assessments/mine/weight-photo`, answers
 * `{ photoPending: isWeightPhotoPending(row) }` — the LEGACY q14 deferral
 * marker and nothing else. It cannot see a `WeightEvidenceRequest` at all. So a
 * patient asked for evidence through the staff lane got `photoPending: false`,
 * both mounts of the upload card stayed unmounted, and the lane the server had
 * just held open for them had no door on it.
 *
 * THIS SET IS NOT A GUESS AT THE ASK, and it must never become one. Inferring
 * "evidence is outstanding" from an adjacent flag is exactly the class of
 * defect `EvidenceState` was written to kill. What it does instead is offer a
 * patient-INITIATED route — no obligation asserted, no clinical claim made —
 * on the surfaces where sending a photo is coherent at all. A patient who was
 * telephoned or emailed can act; a patient who owes nothing sees a quiet
 * optional card in the side rail and ignores it.
 *
 * THE EXCLUSIONS ARE THE ARGUMENT. `empty` has no submitted journey for the
 * server to self-scope to, so the grant would refuse and the control would be a
 * dead button. `rejected` is refused by `assertPhotoLaneOpen` itself (a
 * not-eligible journey takes no photos) and `cancelled` is a plan that is over
 * — offering either an upload invites work that cannot help them, which is the
 * same misjudgement EVIDENCE_ASK_KINDS avoids one register louder.
 *
 * WHAT WOULD RETIRE THIS: clinic-service exposing the outstanding request to
 * the patient — see the [SERVER CONTRACT] block on `showEvidenceSelfServe`.
 * When that read lands, the ASK becomes nameable and this optional route is
 * demoted to what it should be, a convenience.
 */
const EVIDENCE_SELF_SERVE_KINDS: ReadonlySet<DashboardSummaryKind> = new Set<DashboardSummaryKind>([
  'awaiting_review',
  'active_confirmed',
  'payment_incomplete',
  'payment_attention',
  'preauth_expired',
  'generic',
]);

/** The ask. Named so the hero and the card cannot word it differently. */
const EVIDENCE_OWED_NOTE =
  'Your clinician still needs your weight photo before your treatment can be dispatched.';
/** Where to do it: the upload card mounts at the TOP of the grid, above this hero. */
const EVIDENCE_OWED_STEP = 'Add your weight photo using the card at the top of this page.';
/**
 * THE FAILED-READ COPY. It has to clear two bars at once, and most obvious
 * wordings clear only one: it must not read as "all clear" (that is the bug),
 * and it must not invent an obligation either (we do not know that one exists —
 * asserting a photo is owed would send patients who owe nothing hunting for an
 * upload that will never be asked of them). So it reports the failure as OURS,
 * makes no claim about their treatment, and gives both a retry and a route to a
 * human for the patient who was in fact emailed.
 */
const EVIDENCE_UNKNOWN_NOTE =
  'We could not check whether anything else is needed from you right now — that is a problem at our end, '
  + 'not a problem with your treatment.';
const EVIDENCE_UNKNOWN_STEP =
  'Please refresh this page. If we asked you for a photo, contact us and we will help.';
/** In flight. Honest, and gone within a moment — never a standing message. */
const EVIDENCE_CHECKING_STEP = 'Checking whether anything is needed from you…';

/** The summary before the evidence state is folded in. Internal only. */
type BaseSummary = Omit<DashboardSummary, 'evidenceNote' | 'showEvidencePrompt' | 'showEvidenceSelfServe'>;

/**
 * Fold the evidence state into the summary — the single place the two former
 * sources of truth become one.
 *
 * `'none'` is deliberately an IDENTITY on the copy: a patient who owes nothing
 * reads exactly what they read before this change, which is what makes the
 * regression test for it worth having.
 */
function withEvidence(base: BaseSummary, evidence: EvidenceState): DashboardSummary {
  const passive = PASSIVE_NEXT_STEPS.has(base.nextStep);
  const askable = EVIDENCE_ASK_KINDS.has(base.kind);
  /**
   * THE OPTIONAL ROUTE, DECIDED ONCE AND HERE. It is offered only after the
   * status read has RESOLVED — while `'checking'` we do not yet know whether
   * the real ask card is about to mount above it, and swapping one upload card
   * for another under a patient's cursor is worse than a moment's silence. It
   * is never offered alongside the ask itself: every `return` below that sets
   * `showEvidencePrompt: true` sets this false in the same object, so the two
   * cannot both be on the page.
   */
  const selfServe =
    EVIDENCE_SELF_SERVE_KINDS.has(base.kind) && (evidence === 'none' || evidence === 'unknown');

  if (!askable || evidence === 'none') {
    return { ...base, showEvidencePrompt: false, showEvidenceSelfServe: selfServe };
  }

  if (evidence === 'checking') {
    // No note and no card while we are still looking. The only change is that
    // the passive assurance is withheld until it is actually earned — we are
    // not going to print "No action is needed right now." and then take it back.
    return {
      ...base,
      nextStep: passive ? EVIDENCE_CHECKING_STEP : base.nextStep,
      showEvidencePrompt: false,
      showEvidenceSelfServe: false,
    };
  }

  if (evidence === 'unknown') {
    // NOT `showEvidencePrompt: true`. The card's own heading is "Add your weight
    // photo" — mounting it here would state an obligation we cannot stand behind.
    return {
      ...base,
      evidenceNote: EVIDENCE_UNKNOWN_NOTE,
      // `evidenceStep` is set unconditionally; `nextStep` still only yields to
      // it on a passive base. On a non-passive base (the empty account) that is
      // the ONLY carrier of the retry and the route to a human, and without it
      // the admission reaches a hero-less surface as a dead end.
      evidenceStep: EVIDENCE_UNKNOWN_STEP,
      nextStep: passive ? EVIDENCE_UNKNOWN_STEP : base.nextStep,
      showEvidencePrompt: false,
      /* THE FAILED READ IS WHERE THIS EARNS ITS KEEP. The note above admits we
         could not check and tells a patient who WAS emailed to contact us; the
         optional card gives that same patient somewhere to just send it. */
      showEvidenceSelfServe: selfServe,
    };
  }

  // Owed. The note always shows; the step is only overwritten where the base
  // step was passive — "Return to checkout" outranks a photo, and the empty
  // state's "start your check" is the only route to a treatment at all.
  return {
    ...base,
    evidenceNote: EVIDENCE_OWED_NOTE,
    evidenceStep: EVIDENCE_OWED_STEP,
    nextStep: passive ? EVIDENCE_OWED_STEP : base.nextStep,
    // The badge is the scannable half of the hero. Leaving it at "Awaiting
    // clinician approval" over a next step that asks for a photo is the same
    // contradiction one line further up.
    badgeLabel: passive ? 'Action needed' : base.badgeLabel,
    showEvidencePrompt: true,
    // The ask is on the page; the optional route would be a second upload card
    // saying something quieter about the same thing.
    showEvidenceSelfServe: false,
  };
}

/**
 * Derive the top-of-dashboard summary from live dashboard data AND the evidence
 * state. Both are inputs; there is no third source. The caller MUST gate the
 * evidence upload card on the returned `showEvidencePrompt` rather than on its
 * own copy of the status read — see {@link EvidenceState}.
 *
 * Priority: empty → payment attention (skipped once the plan is cancelled) →
 * clinician reject → cancelled plan → incomplete payment → lapsed pre-auth →
 * awaiting review (Route 1) → active/confirmed → generic; then the evidence
 * state is folded in by {@link withEvidence}.
 * Reject and cancel are NOT the same: a clinician `rejected` order is a clinical
 * no; a `cancelled` plan (admin stop, dunning, etc.) must not say the clinician
 * refused treatment.
 */
export function dashboardSummary(data: DashboardData, evidence: EvidenceState): DashboardSummary {
  return withEvidence(baseSummary(data), evidence);
}

/** The plan/order/bill half of the answer. Evidence is folded in by the caller above. */
function baseSummary(data: DashboardData): BaseSummary {
  const plan = data.currentPlan;
  const order = latestOrder(data.orders);
  const bills = data.bills;

  if (!plan && data.orders.length === 0) {
    return {
      kind: 'empty',
      headline: 'No orders yet',
      body: 'Your plan and orders will appear here once you\'ve completed your check.',
      nextStep: 'Start your 2-minute eligibility check when you’re ready.',
      tone: 'muted',
      badgeLabel: 'Get started',
    };
  }

  if (paymentIsFailing(plan, bills)) {
    return {
      kind: 'payment_attention',
      headline: 'Your payment needs attention',
      body: 'We could not take a payment on your plan. Please check your billing history below for details.',
      paymentNote: 'No self-serve card update is available in the portal yet.',
      nextStep: 'If you need help, contact us — we will guide you through the next step.',
      tone: 'danger',
      badgeLabel: 'Action needed',
    };
  }

  if (order?.state === 'rejected') {
    return {
      kind: 'rejected',
      headline: 'Your treatment was not approved',
      body: 'A clinician reviewed your assessment and could not approve this treatment at this time.',
      paymentNote: notChargedNote(bills),
      nextStep: 'If you have questions, contact us and a member of the team will help.',
      tone: 'danger',
      badgeLabel: 'Not approved',
    };
  }

  if (plan?.state === 'cancelled') {
    return {
      kind: 'cancelled',
      headline: 'Your plan has been cancelled',
      body: 'This subscription is no longer active.',
      paymentNote: cancelledPaymentNote(bills),
      nextStep: 'If you have questions, contact us and a member of the team will help.',
      tone: 'muted',
      badgeLabel: 'Cancelled',
    };
  }

  if (order?.state === 'awaiting_payment') {
    return {
      kind: 'payment_incomplete',
      headline: 'Your payment is incomplete',
      body: 'Your order is waiting for payment to finish before a clinician can review it.',
      nextStep: 'Return to checkout when you are ready to complete payment. Resume from the dashboard is not available yet.',
      tone: 'pending',
      badgeLabel: 'Payment incomplete',
    };
  }

  // A LAPSED month-1 pre-authorisation. The sweep that expires a stale hold flips
  // the PaymentRecord to `released` and touches nothing else: the OrderCase stays
  // `awaiting_approval` for ever and no lane will ever pick it up again
  // (orders#177). Without this branch the patient reads "being reviewed" and "no
  // action is needed right now" indefinitely for a case that is dead — and no
  // email goes out either, so this card is the only place they can learn the
  // truth.
  //
  // Why the released/awaiting_approval PAIR is safe to read as a lapse: the only
  // other ways a pre-auth ends up `released` are a clinician reject and a
  // cancelled plan, and both are matched ABOVE (`order.state === 'rejected'`,
  // `plan?.state === 'cancelled'`), so neither can reach here — and a rejected
  // order would no longer be `awaiting_approval` in the first place.
  //
  // The `!held` conjunct is not belt-and-braces. `bills` is the ACCOUNT's payment
  // history, not this order's (`orderCaseId` is an optional [CONTRACTS GAP]), so a
  // returning patient whose earlier order was rejected carries a `released` row
  // for ever. Their live new order — awaiting approval with the hold in place —
  // would otherwise be declared expired, and we would tell someone to ring us
  // about a review that is proceeding perfectly normally. A real lapse leaves no
  // `held` row behind for the order it killed (the sweep flips that row in place),
  // so requiring no live hold costs the genuine case nothing.
  if (order?.state === 'awaiting_approval' && hasPhase(bills, 'released') && !hasPhase(bills, 'held')) {
    return {
      kind: 'preauth_expired',
      headline: 'Your payment authorisation has expired',
      body:
        'Your card was authorised when you placed this order, but it was never charged — and that '
        + 'authorisation has now expired. This order cannot be reviewed as it stands, and we are sorry it '
        + 'has been left waiting.',
      // Deliberately definite, not `notChargedNote(bills)`: that helper hedges
      // ("Any payment hold…") because its callers may have no bill at all, and a
      // hedge is the wrong register when the released row is right here in front
      // of us. Being charged is the first thing this patient will worry about.
      paymentNote: 'You have not been charged — the hold on your card has been released.',
      nextStep: 'Contact us and we will get your treatment restarted.',
      tone: 'danger',
      badgeLabel: 'Action needed',
    };
  }

  // Route 1 — dormant plan and/or awaiting clinician approval, typically with a held payment.
  if (plan?.state === 'dormant' || order?.state === 'awaiting_approval') {
    const held = hasPhase(bills, 'held');
    return {
      kind: 'awaiting_review',
      headline: 'Your treatment is being reviewed',
      body: 'A clinician is reviewing your assessment before treatment can be approved.',
      paymentNote: held
        ? 'Your payment is authorised but has not been charged yet.'
        : 'Nothing is charged until a clinician approves your treatment.',
      nextStep: NO_ACTION_NEEDED,
      tone: 'pending',
      badgeLabel: 'Awaiting clinician approval',
    };
  }

  if (plan?.state === 'active' || order?.state === 'approved') {
    const paid = hasPhase(bills, 'captured');
    return {
      kind: 'active_confirmed',
      headline: paid ? 'Your treatment is confirmed' : 'Your treatment is approved',
      body: paid
        ? 'Your clinician approved this treatment and payment has been taken.'
        : 'Your clinician approved this treatment. You can track your plan and orders below.',
      // ⚠️ "Your payment is complete." WAS FALSE FOR THE COMMONEST PATIENT ON
      // THIS SCREEN (2026-09-05 review, defect 4). The moment a clinician
      // approves, the plan goes `active`, the month-1 hold captures and
      // `monthsRemaining` drops from 3 to 2 — so this note fired for a patient
      // whose plan card, one column over, was reading "Months remaining: 2" and
      // "Next payment: 15 September", and whose stop-months panel says "the 2
      // months still to run are charged as they fall due". One captured bill is
      // not a settled account on a committed term.
      //
      // The remaining-months clause is the panel's OWN clause — `chargedClause`,
      // the same function `chargedAndSentClause` is built from — not a
      // paraphrase of it, so neither the wording nor the singular agreement can
      // drift between the note above the fold and the panel below it.
      // `monthsRemaining === 0` (and an approved order with no plan at all) is
      // the case the original sentence was actually true for; it keeps it.
      paymentNote: paid
        ? plan && plan.monthsRemaining > 0
          ? `Your payment has been taken — ${chargedClause(plan.monthsRemaining)}.`
          : 'Your payment is complete.'
        : undefined,
      nextStep: TRACK_BELOW,
      tone: 'positive',
      badgeLabel: paid ? 'Confirmed' : 'Approved',
    };
  }

  // ⚠️ THIS BRANCH IS LARGELY UNREACHABLE, AND IT IS NOT THIS CHANGE'S TO FIX
  // (found 2026-09-05 while reading hero and panel side by side in every
  // state). `plan?.state === 'active' || order?.state === 'approved'` sits
  // ABOVE it, and a completed plan's last order is `approved` — so a patient
  // whose term has finished reads "Your treatment is confirmed" and "Track your
  // plan, orders and billing below" over a plan card showing "Months remaining:
  // —" and a "Completed" badge. Only a completed plan whose latest order is NOT
  // approved reaches this wording.
  //
  // Left alone deliberately: the stop-months panel never renders for a
  // `completed` plan (`canStop` excludes terminal states), so this is outside
  // the hero-vs-panel disagreement being fixed here — and reordering it changes
  // what an entire cohort reads and interacts with the re-subscribe lane, which
  // is a product decision rather than a copy correction.
  if (plan?.state === 'completed') {
    return {
      kind: 'generic',
      headline: 'Your plan is complete',
      body: 'This subscription term has finished. Past orders and billing stay available below.',
      nextStep: 'Start a new eligibility check if you would like to continue treatment.',
      tone: 'muted',
      badgeLabel: 'Completed',
    };
  }

  return {
    kind: 'generic',
    headline: 'Your dashboard',
    body: 'Here is an overview of your plan, orders and billing.',
    nextStep: REVIEW_DETAILS,
    tone: 'neutral',
    badgeLabel: 'Overview',
  };
}

/**
 * Plan card field presentation — dormant plans must not look like billing has started.
 * Display strings are patient-ready (dates already formatted).
 */
export function planCardPresentation(plan: {
  state: string;
  monthsRemaining: number;
  nextPaymentDate: string | null;
}): {
  monthsRemainingValue: string;
  nextPaymentValue: string;
  help?: string;
} {
  if (plan.state === 'dormant') {
    return {
      monthsRemainingValue: 'Starts after approval',
      nextPaymentValue: 'After clinician approval',
      help: 'Your subscription and billing start only once a clinician approves your treatment.',
    };
  }
  // Terminal plans (cancelled on reject/dunning, or a completed term) have no
  // remaining months and nothing left to bill — never show a stale countdown.
  if (plan.state === 'cancelled' || plan.state === 'completed') {
    return {
      monthsRemainingValue: '—',
      nextPaymentValue: '—',
    };
  }
  return {
    monthsRemainingValue: String(plan.monthsRemaining),
    nextPaymentValue: formatUtcDate(plan.nextPaymentDate) || '—',
  };
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * "Stop future months" — the confirm copy, keyed on what is TRUE FOR THIS
 * PATIENT rather than one flat sentence for everybody (CTO 2026-09-05).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ READ THIS BEFORE CHANGING A WORD OF IT. This is a payments screen, and the
 * two ways to get it wrong — telling a patient their card will be charged when
 * it will not, and telling them it will not when it will — are the worst
 * defects available here. Every sentence below is checked against what
 * orders-service actually does, not against what the control's NAME suggests.
 *
 * ── WHAT `POST /v1/subscriptions/:id/cancel` ACTUALLY DOES. It sets
 * `cancelAtTermEnd: true` and NOTHING ELSE. It does not clear `nextBillingAt`,
 * and `runDue` selects on `state ∈ BILLING_LIVE && nextBillingAt <= now` with no
 * regard for the flag — so every month left in the committed term STILL BILLS.
 * All the flag changes is the label the plan lands on at term end (`cancelled`
 * rather than `completed`), the Stripe customer marker, and re-subscribe
 * eligibility. The service's own docblock says so: "leaves the plan billing to
 * the end of its commitment".
 *
 * ⚠️ SO THE COPY THIS REPLACED WAS WRONG IN THE PATIENT'S FAVOUR AND AGAINST
 * THE TRUTH. It said "You will keep the months you have already been charged
 * for… The months after that will not be charged." A patient in month 1 of 3
 * read that as "months 2 and 3 will not be charged". They will be. (It also
 * pluralised "months you have already been charged for" off `monthsRemaining`,
 * which counts the months NOT yet charged.)
 *
 * ── THE STATE-AWARE HALF. The one thing that genuinely differs between two
 * patients here is whether there is money sitting AUTHORISED on the card that
 * has not been taken: the first month is authorised at checkout and captured on
 * approval, so a plan awaiting a clinician has a live hold, and a patient
 * mid-term does not. That is read from the BILLING HISTORY — a `held` bill —
 * and NOT from `plan.state`, deliberately: the dashboard hero already draws its
 * "Your payment is authorised but has not been charged yet" line from exactly
 * that field, and a card that disagreed with the hero six inches above it would
 * be worse than either sentence alone. A dormant plan with no hold (a lane that
 * never took one) correctly gets no charge-pending line.
 *
 * ── AND THE TERM SENTENCE IS STATE-AWARE TOO (2026-09-05 review, defect 2).
 * `chargePending` was made state-aware and `term` was left flat, which put the
 * exact hero-vs-card disagreement this docblock exists to prevent back on the
 * screen one sentence later. `canStop` (Dashboard.tsx) renders this control for
 * `dormant`, `active` AND `past_due` — and EVERY patient passes through
 * `dormant` between checkout and review — so the one flat sentence ("the N
 * months still to run are charged as they fall due, and the treatment for each
 * of them is still sent to you") was also being told to:
 *
 *   • a DORMANT patient, six inches under a hero reading "Nothing is charged
 *     until a clinician approves your treatment". Neither half is true yet.
 *     `runDue` selects on `state ∈ BILLING_LIVE`, which is `['active',
 *     'past_due']` and EXCLUDES `dormant` (orders `order-states.ts`), and the
 *     repeat-Rx engine reads `state: 'active'` only (`prescriptions.service
 *     .ts`). A dormant plan charges nothing and ships nothing until an approval
 *     activates it — and the approval is the thing that might not happen.
 *
 *   • a PAST_DUE patient, under a hero reading "We could not take a payment on
 *     your plan". "The treatment for each of them is still sent to you" is
 *     FALSE while the plan is parked — the repeat-Rx engine skips a `past_due`
 *     sub outright — and the months are not simply "charged as they fall due"
 *     either: `markPastDue` runs a bounded backoff ladder (~1/3/7 days) and at
 *     `MAX_RENEWAL_ATTEMPTS` gives the plan up to the terminal `cancelled`
 *     state with `reason: dunning_exhausted`. The term can END EARLY, with the
 *     months after it neither charged nor sent.
 *
 * ── WHY `plan.state`, WHEN `chargePending` DELIBERATELY READS THE BILLS.
 * Different subjects. `chargePending`'s subject IS a payment sitting on a card,
 * so a `held` bill is the fact. This sentence's subject is the SUBSCRIPTION, and
 * `runDue`, the repeat-Rx engine and the dunning ladder all gate on
 * `Subscription.state` — the bills cannot answer it.
 *
 * ── AND WHERE THE HERO ASKS A WIDER QUESTION, SO DOES THIS (2026-09-05 review,
 * defect 4). The hero's payment-attention branch is `plan.state === 'past_due'
 * || hasPhase(bills, 'failed')`; the term sentence keyed on the first half only,
 * on the argument that orders writes `PaymentRecord.phase` as
 * held/captured/released/refunded and no path writes `'failed'`. That argument
 * is about ANOTHER SERVICE and this repo cannot hold it: `BillPhase` admits
 * `'failed'`, `patientStatusCopy` renders it and `BillsCard` lists it. Both
 * surfaces now call {@link paymentIsFailing}, so the panel cannot key on a
 * narrower condition than the hero it has to agree with. The SUPPLY half stays
 * keyed on `past_due` alone — see the comment in {@link termSentence}: dunning
 * and the repeat-Rx engine are different engines reading different fields.
 *
 * ── WHAT COULD NOT BE DISTINGUISHED, AND IS THEREFORE SAID CONDITIONALLY.
 *   1. WHETHER THE CLINICIAN WILL APPROVE. Nothing on the dashboard predicts it
 *      (and an approval is not even blocked by this stop — `approve()` activates
 *      a dormant plan whatever `cancelAtTermEnd` says), so the dormant sentence
 *      states BOTH outcomes rather than the likelier one.
 *   2. WHETHER A PAST_DUE PLAN RECOVERS OR LAPSES. The dunning position is not
 *      on the patient wire shape — `CurrentPlan` carries no `retryCount` — so
 *      the dashboard cannot say which attempt this is or how many are left. The
 *      past_due sentence therefore has to hold at both ends of the ladder.
 *   3. WHICH ORDER THE HERO IS SPEAKING ABOUT. The hero mixes `plan.state` with
 *      the LATEST order's state; this card is only ever about the plan directly
 *      above it. They coincide for every single-order patient, which is all of
 *      them today.
 *      ⚠️ THIS IS THE ONE HERO-VS-PANEL MISMATCH LEFT, AND IT IS THE HERO'S
 *      SCOPE, NOT THE PANEL'S. A patient with an ACTIVE plan whose LATEST order
 *      is `rejected` (or `awaiting_payment`) reads "You have not been charged
 *      for this treatment" over a panel that correctly says the running term
 *      still bills. Both sentences are true — of different orders. The panel
 *      must not be "fixed" to match, because the plan really is billing; the
 *      fix, if a multi-order patient ever exists, is to scope the hero to the
 *      plan's own order (`plan.orderCaseId`), which changes the hero for every
 *      patient and belongs in its own change.
 *
 * ── WHAT IS NOT SAID, AND WHY. There is still no "nothing more will be charged"
 * branch: the control only appears while `monthsRemaining > 0`, and on a running
 * term every one of those months bills. If the product ever gains a true
 * mid-term stop, this is the function that has to change with it.
 */
export interface StopFutureMonthsCopy {
  /**
   * The authorised-but-not-taken payment, or null when there is no live hold.
   * WHETHER it renders is keyed on a `held` BILL; the tail of the sentence is
   * keyed on the state, because only a dormant plan is still waiting on the
   * approval that captures it.
   */
  chargePending: string | null;
  /**
   * What the committed term still costs AND still ships — keyed on `plan.state`
   * and on whether a payment is failing, because "charged as they fall due" and
   * "still sent to you" are both false for a plan that has not started, and the
   * first of them is not a promise we can make while a card is failing. It says
   * nothing about how the term ENDS; that is `ending`'s single job.
   */
  term: string;
  /**
   * How and when the plan finishes — the ONE account of it, state-aware since
   * the 2026-09-05 review. See {@link endingSentence}: a `dormant` plan's end is
   * conditional on the approval that starts it, and a failing plan may end on
   * the dunning ladder before its term does.
   */
  ending: string;
  /**
   * The note shown INSTEAD of the panel once the patient has already stopped.
   * Built from `ending`, so the promise made at the confirm and the promise made
   * afterwards are the same sentence.
   */
  alreadyStopped: string;
  /** Never a refund — the sentence the old copy already got right. */
  refund: string;
}

/**
 * Is there money authorised on this patient's card that has not been taken?
 *
 * Scoped to the plan's own order where BOTH sides carry an id — a `held` bill
 * against a different order is a different plan's money. Either side missing
 * falls back to the ungrouped reading, which is the same fallback `BillsCard`
 * documents for a contract-conformant backend that omits `orderCaseId`.
 */
function hasLiveHold(plan: { orderCaseId?: string }, bills: BillSummary[]): boolean {
  return bills.some(
    (b) =>
      b.phase === 'held' &&
      (plan.orderCaseId === undefined || b.orderCaseId === undefined || b.orderCaseId === plan.orderCaseId),
  );
}

/**
 * "3 months" / "1 month". The old copy interpolated `${plan.months} months`
 * unconditionally, so a one-month term read "committed for 1 months" — and the
 * singular branch it did have was keyed on months REMAINING, which is a
 * different number.
 */
function monthsPhrase(n: number): string {
  return n === 1 ? '1 month' : `${n} months`;
}

/**
 * The MONEY half on its own: what a running, paid-up term still bills.
 *
 * Split out because the HERO says it too (`active_confirmed`'s paid note), and
 * the hero and the panel it sits above must not pluralise one remainder two
 * different ways — "The 1 month still to run are charged" is exactly the kind of
 * slip a second, hand-typed copy of this clause produces.
 */
function chargedClause(left: number): string {
  return left === 1
    ? 'the 1 month still to run is charged when it falls due'
    : `the ${left} months still to run are charged as they fall due`;
}

/**
 * The clause that is true of a plan whose term is RUNNING and paid up: every
 * month left bills, and every month left ships.
 *
 * It is quoted VERBATIM inside the dormant sentence, because an approval puts
 * that patient in exactly this position — two wordings of one promise drift,
 * and this is the promise that has already been wrong once.
 */
function chargedAndSentClause(left: number): string {
  return left === 1
    ? `${chargedClause(left)}, and that month's treatment is still sent to you`
    : `${chargedClause(left)}, and the treatment for each of them is still sent to you`;
}

/**
 * The term sentence for THIS plan's state — WHAT THE COMMITTED TERM STILL COSTS
 * AND STILL SHIPS, and nothing about how it finishes.
 *
 * The money and the supply stay in one breath (the two halves the CTO asked for
 * — "what they have already ordered still ships", "their card is going to be
 * charged" — invite reading one without the other if split), but WHICH breath
 * depends on whether the term has not started, is running, or is failing.
 *
 * ⚠️ THE ENDING LEFT THIS SENTENCE (2026-09-05 review, defect 3). The past_due
 * wording used to close with "if we cannot take it your plan ends there —
 * nothing after that is charged or sent", one line above an `ending` sentence
 * asserting the opposite unconditionally ("After your last committed month your
 * plan ends"). Two accounts of how one plan finishes, in adjacent paragraphs, is
 * the paragraph-to-paragraph disagreement this whole change exists to remove.
 * There is now exactly ONE account and it lives in {@link endingSentence} — so
 * do not put an "and then it ends" clause back in here for any state.
 *
 * Terminal states never reach here: `canStop` refuses to render the control for
 * `cancelled`/`completed`. They fall through to the running-term sentence only
 * so the function is total.
 */
function termSentence(state: SubscriptionState, months: number, left: number, failing: boolean): string {
  const committed = `Your plan is committed for ${monthsPhrase(months)}, and stopping does not end it early`;

  // NOT STARTED. Both halves of the running-term sentence are false today and
  // both become true on approval, so the sentence carries its condition.
  //
  // ⚠️ THIS BRANCH RUNS BEFORE THE FAILING ONE, AND THE ORDER IS THE POINT. A
  // dormant plan owes nothing — `runDue` excludes it from BILLING_LIVE — so
  // "the 3 months still to run are still owed" would be false for it even with
  // a failed bill on the account, and the conditional below promises nothing
  // for the hero to contradict in the first place.
  if (state === 'dormant') {
    return (
      `${committed}. Nothing is charged and no treatment is sent unless a clinician approves your `
      + `treatment — but if they do, ${chargedAndSentClause(left)}.`
    );
  }

  // A PAYMENT IS FAILING. The retries are bounded, so this must promise the
  // charge conditionally rather than outright.
  if (failing) {
    const owed =
      left === 1
        ? 'the 1 month still to run is still owed, and we will keep trying to take it'
        : `the ${left} months still to run are still owed, and we will keep trying to take them as they fall due`;
    // ⚠️ THE SUPPLY HALF IS KEYED ON `past_due` AND NOT ON `failing`, BECAUSE
    // THE TWO ARE DECIDED BY DIFFERENT ENGINES. Dunning keys on payment; the
    // repeat-Rx engine reads `state: 'active'` and skips a `past_due` sub
    // outright. So a plan the ladder has parked ships nothing, and an `active`
    // plan carrying a failed bill still ships — saying "no further treatment is
    // sent" there would be a fresh lie, in the patient's disfavour, told to fix
    // one in their favour.
    const supply =
      state === 'past_due'
        ? 'While a payment has not gone through, no further treatment is sent.'
        : 'Your treatment carries on being sent while your plan is running.';
    return `${committed}: ${owed}. ${supply}`;
  }

  return `${committed}: ${chargedAndSentClause(left)}.`;
}

/**
 * THE ONE ACCOUNT OF HOW THE PLAN FINISHES (2026-09-05 review, defect 3).
 *
 * ⚠️ THIS USED TO BE A FLAT STRING, DELIBERATELY — and the deliberateness was
 * the defect. `term` was made state-aware and `ending` was held out of the
 * branch, with a test pinning it as "state-independent". But whether the plan
 * runs to its last committed month is not a constant: a `past_due` plan can die
 * on the dunning ladder at `MAX_RENEWAL_ATTEMPTS` (`reason: dunning_exhausted`)
 * and a `dormant` plan may never start at all, so the flat sentence contradicted
 * the past_due term sentence directly above it and over-promised to every
 * patient waiting on a clinician.
 *
 * Each branch names the endpoint that its own `term` sentence implies, and only
 * that endpoint:
 *   • DORMANT — the term begins only on an approval, so its end is conditional
 *     on the same thing. Asserting a last committed month tells a patient who
 *     may be turned down that they have a term running.
 *   • PAYMENT FAILING — states BOTH ends of the ladder, because the dashboard
 *     cannot tell which one this patient is heading for (`CurrentPlan` carries
 *     no `retryCount`), and closes on what is true either way.
 *   • RUNNING — the original sentence, which was right for this state all along.
 *   • TERMINAL — `canStop` never renders the confirm panel here, but the
 *     already-stopped note does render for a plan that has since ended, and
 *     "after your last committed month your plan ends" is a promise in the wrong
 *     TENSE for a plan that finished last month.
 */
function endingSentence(state: SubscriptionState, failing: boolean): string {
  if (state === 'cancelled' || state === 'completed') {
    return 'Your plan has now ended — nothing further is charged and no more treatment is sent.';
  }
  if (state === 'dormant') {
    return (
      'If a clinician approves your treatment, your plan ends after its last committed month — '
      + 'nothing further is charged and no more treatment is sent.'
    );
  }
  if (failing) {
    return (
      'Your plan ends after your last committed month — or sooner, if we cannot take a payment. '
      + 'Either way, nothing further is charged and no more treatment is sent after that.'
    );
  }
  return 'After your last committed month your plan ends — nothing further is charged and no more treatment is sent.';
}

export function stopFutureMonthsCopy(
  plan: { months: number; monthsRemaining: number; state: SubscriptionState; orderCaseId?: string },
  bills: BillSummary[],
): StopFutureMonthsCopy {
  const left = Math.max(0, plan.monthsRemaining);
  const failing = paymentIsFailing(plan, bills);
  const ending = endingSentence(plan.state, failing);
  const terminal = plan.state === 'cancelled' || plan.state === 'completed';
  return {
    chargePending: hasLiveHold(plan, bills)
      ? plan.state === 'dormant'
        // The designed case: the month-1 pre-auth, waiting on the review the
        // hero six inches above is also talking about.
        ? 'Your first payment is authorised on your card and has not been taken yet. It will still be charged when a clinician approves your treatment.'
        // ⚠️ AND THE CASE THAT CONTRADICTED THE HERO (2026-09-05 review, defect
        // 4). A `held` bill on an ALREADY-APPROVED plan told the patient their
        // money "will still be charged when a clinician approves your
        // treatment" underneath a hero reading "Your clinician approved this
        // treatment" — an approval described as still to come, one that has
        // already happened. It is not their "first" payment either, mid-term.
        // What is true in every non-dormant case is the thing this line exists
        // to say: stopping does not give the money back.
        : 'A payment is authorised on your card and has not been taken yet. Stopping future months does not release it.'
      : null,
    term: termSentence(plan.state, plan.months, left, failing),
    ending,
    // The already-stopped note is BUILT FROM `ending` rather than typed out, so
    // the sentence a patient reads before they press and the sentence they read
    // afterwards cannot say different things about the same plan. It used to be
    // hard-coded in Dashboard.tsx as "Your plan is set to end after its
    // committed term" — the same over-promise `ending` carried, in a second
    // place, where a dormant patient who may never be approved and a past_due
    // patient the ladder may cancel next week were both told their term will
    // run to the end.
    alreadyStopped:
      `${terminal ? '' : 'You have asked us to stop future months. '}${ending} `
      + 'To carry on, call us and we will restart it.',
    refund: 'This does not refund anything you have already paid.',
  };
}
